import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Offcanvas, Button, Badge, Alert, Table, Row, Col, Card } from 'react-bootstrap';
import './ActivityLogs.css';

const ActivityLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        model_type: '',
        action: '',
        search: '',
        page: 1,
        per_page: 25
    });
    const [statistics, setStatistics] = useState({
        model_types: [],
        actions: [],
        recent_users: []
    });
    const [selectedLog, setSelectedLog] = useState(null);
    const [showOffcanvas, setShowOffcanvas] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [pagination, setPagination] = useState({
        total: 0,
        per_page: 25,
        current_page: 1,
        last_page: 1
    });
    const lastFetchedParamsRef = useRef('');
    
    // Fetch logs
    const fetchLogs = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            let params = { page: page || filters.page };

            if (filters.model_type) params.model_type = filters.model_type;
            if (filters.action) params.action = filters.action;
            if (filters.user_id) params.user_id = filters.user_id;
            if (filters.start_date) params.start_date = filters.start_date;
            if (filters.end_date) params.end_date = filters.end_date;
            if (filters.search) params.search = filters.search;
            if (filters.per_page) params.per_page = filters.per_page;

            const paramsString = JSON.stringify(params);
            if (lastFetchedParamsRef.current === paramsString) return;
            lastFetchedParamsRef.current = paramsString;

            const response = await axios.get(`${backendUrl}/api/activity-logs`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params,
            });

            if (response.data.success) {
                const { logs, statistics } = response.data;

                setLogs(logs.data);
                setStatistics(statistics);
                setPagination({
                    total: logs.total,
                    per_page: logs.per_page,
                    current_page: logs.current_page,
                    last_page: logs.last_page
                });

                setFilters(prev => ({ ...prev, page: logs.current_page }));
            }
        } catch (error) {
            console.error('Error fetching activity logs:', error);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchLogs(filters.page);
    }, [fetchLogs, filters.page]);

    const handlePageChange = (page) => {
        setFilters(prev => ({
            ...prev,
            page: page
        }));
    };

    const handleFilterChange = (name, value) => {
        setFilters(prev => ({
            ...prev,
            [name]: value,
            page: 1
        }));
    };

    const handleResetFilters = () => {
        setFilters({
            model_type: '',
            action: '',
            search: '',
            page: 1,
            per_page: 25
        });
    };

    const openLogDetails = (log) => {
        setSelectedLog(log);
        setShowOffcanvas(true);
    };

    const closeOffcanvas = () => {
        setShowOffcanvas(false);
        setTimeout(() => {
            setSelectedLog(null);
            setActiveTab('overview');
        }, 300);
    };

    const getActionBadgeClass = (action) => {
        switch (action) {
            case 'created': return 'badge-success';
            case 'updated': return 'badge-warning';
            case 'deleted': return 'badge-danger';
            default: return 'badge-secondary';
        }
    };

    const getActionBadgeColor = (action) => {
        switch(action) {
            case 'created': return 'success';
            case 'updated': return 'warning';
            case 'deleted': return 'danger';
            default: return 'secondary';
        }
    };

    const formatDate = (dateString) => {
        return format(new Date(dateString), 'PPpp');
    };

    const getModelIcon = (modelType) => {
        const icons = {
            'App\\Models\\Transaction': '📊',
            'App\\Models\\User': '👤',
            'App\\Models\\Client': '🏢',
            'App\\Models\\ProformaInvoice': '🧾',
            'App\\Models\\ProjectExpense': '💰',
            'App\\Models\\Category': '📂',
            'App\\Models\\BankAccount': '🏦',
        };
        return icons[modelType] || '📄';
    };

    // Helper function to filter out unimportant fields
    const filterUnimportantFields = (data) => {
        if (!data || Object.keys(data).length === 0) return {};
        
        const unimportantFields = [
            'id', '_id', 'created_at', 'updated_at', 'deleted_at', 
            'created_by', 'updated_by', 'deleted_by',
            'createdAt', 'updatedAt', 'deletedAt',
            'tenant_id', 'company_id', 'branch_id',
            'remember_token', 'email_verified_at', 'password',
            'password_confirmation', 'token', 'api_token',
            'deletedAt', 'deleted_by'
        ];
        
        const filteredData = {};
        Object.entries(data).forEach(([key, value]) => {
            // Skip if field is in unimportant list or ends with _id (foreign keys)
            if (!unimportantFields.includes(key) && 
                !key.endsWith('_id') && 
                value !== undefined && 
                value !== null) {
                filteredData[key] = value;
            }
        });
        
        return filteredData;
    };

    // Helper function to format field names for display
    const formatFieldName = (fieldName) => {
        return fieldName
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    };

    // Helper function to get field-specific display value
    const formatFieldValue = (fieldName, value) => {
        if (value === null || value === undefined || value === '') return '—';
        
        // Handle dates
        if (fieldName.includes('date') || fieldName.includes('Date') || 
            fieldName.includes('_at') || fieldName.endsWith('_date')) {
            try {
                return format(new Date(value), 'PPpp');
            } catch {
                return String(value);
            }
        }
        
        // Handle amounts/currency fields
        if (fieldName.includes('amount') || fieldName.includes('price') || 
            fieldName.includes('total') || fieldName.includes('balance') ||
            fieldName.includes('cost') || fieldName.includes('value')) {
            if (typeof value === 'number') {
                return new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                }).format(value);
            }
        }
        
        // Handle boolean values
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        
        // Handle JSON/array values
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch {
                return String(value);
            }
        }
        
        // Truncate long strings
        const strValue = String(value);
        if (strValue.length > 50) {
            return strValue.substring(0, 47) + '...';
        }
        
        return strValue;
    };

    // Helper function to get changed fields only (for updated action)
    const getChangedFields = (oldData, newData) => {
        const changedFields = {};
        
        if (!oldData || !newData) return changedFields;
        
        // Filter out unimportant fields first
        const filteredOldData = filterUnimportantFields(oldData);
        const filteredNewData = filterUnimportantFields(newData);
        
        // Check all keys in new data
        Object.keys(filteredNewData).forEach(key => {
            const oldValue = filteredOldData[key];
            const newValue = filteredNewData[key];
            
            // Check if value has changed (handle different data types)
            let hasChanged = false;
            
            if (oldValue === undefined && newValue !== undefined) {
                hasChanged = true; // Field was added
            } else if (oldValue !== undefined && newValue === undefined) {
                hasChanged = true; // Field was removed
            } else if (oldValue !== newValue) {
                // Deep comparison for objects/arrays
                if (typeof oldValue === 'object' && typeof newValue === 'object') {
                    hasChanged = JSON.stringify(oldValue) !== JSON.stringify(newValue);
                } else {
                    hasChanged = oldValue !== newValue;
                }
            }
            
            if (hasChanged) {
                changedFields[key] = {
                    old: oldValue,
                    new: newValue
                };
            }
        });
        
        // Also check for fields that were in old data but not in new data
        Object.keys(filteredOldData).forEach(key => {
            if (filteredNewData[key] === undefined && !changedFields[key]) {
                changedFields[key] = {
                    old: filteredOldData[key],
                    new: undefined
                };
            }
        });
        
        return changedFields;
    };

    // Helper function to get important fields for created action
    const getImportantCreatedFields = (data) => {
        if (!data) return {};
        
        const filteredData = filterUnimportantFields(data);
        
        // Define which fields are most important to show first
        const importantFieldOrder = [
            'name', 'title', 'amount', 'total', 'price', 'narration', 'description',
            'email', 'phone', 'status', 'type', 'date', 'transaction_date',
            'invoice_number', 'reference_number', 'account_number', 'bank_name'
        ];
        
        // Sort fields by importance
        const sortedEntries = Object.entries(filteredData).sort(([keyA], [keyB]) => {
            const indexA = importantFieldOrder.indexOf(keyA);
            const indexB = importantFieldOrder.indexOf(keyB);
            
            if (indexA === -1 && indexB === -1) return 0;
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
        
        return Object.fromEntries(sortedEntries.slice(0, 10)); // Limit to 10 fields
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0 font-size-18">Logs</h4>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="card"></div>
                            <div className="activity-logs-container">
                                <div className="activity-logs-header">
                                    <h1 className="page-title">
                                        <span className="page-icon">📋</span> Activity Logs
                                    </h1>
                                    <div className="header-actions">
                                        <button className="btn btn-outline-primary" onClick={() => { }}>
                                            <span className="btn-icon">⬇️</span> Export
                                        </button>
                                        <button className="btn btn-primary" onClick={fetchLogs}>
                                            <span className="btn-icon">🔄</span> Refresh
                                        </button>
                                    </div>
                                </div>

                                <div className="stats-grid">
                                    <div className="stat-card">
                                        <div className="stat-icon">📈</div>
                                        <div className="stat-content">
                                            <div className="stat-label">Total Logs</div>
                                            <div className="stat-value">{pagination.total}</div>
                                        </div>
                                    </div>
                                    <div className="stat-card">
                                        <div className="stat-icon">📁</div>
                                        <div className="stat-content">
                                            <div className="stat-label">Model Types</div>
                                            <div className="stat-value">{statistics.model_types.length}</div>
                                        </div>
                                    </div>
                                    <div className="stat-card">
                                        <div className="stat-icon">👥</div>
                                        <div className="stat-content">
                                            <div className="stat-label">Users</div>
                                            <div className="stat-value">{statistics.recent_users.length}</div>
                                        </div>
                                    </div>
                                    <div className="stat-card">
                                        <div className="stat-icon">🕒</div>
                                        <div className="stat-content">
                                            <div className="stat-label">Last Updated</div>
                                            <div className="stat-value">
                                                {logs[0] ? formatDate(logs[0].created_at) : 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="filters-card">
                                    <div className="filters-header">
                                        <h3>🔍 Filters</h3>
                                        <button className="btn btn-sm btn-outline-secondary" onClick={handleResetFilters}>
                                            Clear All
                                        </button>
                                    </div>

                                    <div className="filters-grid">
                                        <div className="filter-group">
                                            <label>Model Type</label>
                                            <select
                                                value={filters.model_type}
                                                onChange={(e) => handleFilterChange('model_type', e.target.value)}
                                                className="form-select"
                                            >
                                                <option value="">All Models</option>
                                                {statistics.model_types.map((model) => (
                                                    <option key={model.model_type} value={model.model_type}>
                                                        {model.model_type.split('\\').pop()} ({model.count})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="filter-group">
                                            <label>Action</label>
                                            <select
                                                value={filters.action}
                                                onChange={(e) => handleFilterChange('action', e.target.value)}
                                                className="form-select"
                                            >
                                                <option value="">All Actions</option>
                                                {statistics.actions.map((action) => (
                                                    <option key={action.action} value={action.action}>
                                                        {action.action} ({action.count})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="filter-group">
                                            <label>Search</label>
                                            <div className="search-input">
                                                <span className="search-icon">🔍</span>
                                                <input
                                                    type="text"
                                                    placeholder="Search..."
                                                    value={filters.search}
                                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                                    className="form-control"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="quick-filters">
                                        <span className="quick-filters-label">Quick Filters:</span>
                                        <div className="quick-filters-chips">
                                            {statistics.model_types.slice(0, 5).map((model) => (
                                                <button
                                                    key={model.model_type}
                                                    className={`chip ${filters.model_type === model.model_type ? 'chip-active' : ''}`}
                                                    onClick={() => handleFilterChange('model_type', model.model_type)}
                                                >
                                                    {getModelIcon(model.model_type)} {model.model_type.split('\\').pop()}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="table-container">
                                    <div className="table-responsive">
                                        <table className="logs-table">
                                            <thead>
                                                <tr>
                                                    <th>Date & Time</th>
                                                    <th>User</th>
                                                    <th>Action</th>
                                                    <th>Model</th>
                                                    <th>Record</th>
                                                    <th>IP Address</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="7" className="loading-cell">
                                                            <div className="spinner"></div>
                                                            Loading...
                                                        </td>
                                                    </tr>
                                                ) : logs.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="7" className="empty-cell">
                                                            No activity logs found
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    logs.map((log) => (
                                                        <tr key={log.id} className="log-row">
                                                            <td>{formatDate(log.created_at)}</td>
                                                            <td>
                                                                <div className="user-cell">
                                                                    <div className="user-avatar">
                                                                        {log.user?.name?.charAt(0) || 'U'}
                                                                    </div>
                                                                    <div className="user-info">
                                                                        <div className="user-name">{log.user?.name || 'Unknown'}</div>
                                                                        <div className="user-email">{log.user?.email}</div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className={`badge ${getActionBadgeClass(log.action)}`}>
                                                                    {log.action}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="model-cell">
                                                                    <span className="model-icon">{getModelIcon(log.model_type)}</span>
                                                                    <span>{log.model_type.split('\\').pop()}</span>
                                                                </div>
                                                            </td>
                                                            <td>ID: {log.model_id}</td>
                                                            <td>
                                                                <code>{log.ip_address}</code>
                                                            </td>
                                                            <td>
                                                                <div className="action-buttons">
                                                                    <Button
                                                                        variant="info"
                                                                        size="sm"
                                                                        onClick={() => openLogDetails(log)}
                                                                    >
                                                                        👁️ View
                                                                    </Button>
                                                                    <Button
                                                                        variant="secondary"
                                                                        size="sm"
                                                                        className="ms-1"
                                                                        onClick={() => window.open(`/dashboard/${log.model_type.split('\\').pop().toLowerCase()}s/${log.model_id}`, '_blank')}
                                                                    >
                                                                        📊 Model
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {!loading && logs.length > 0 && (
                                        <div className="pagination">
                                            <button
                                                className="page-btn"
                                                disabled={pagination.current_page === 1}
                                                onClick={() => handlePageChange(pagination.current_page - 1)}
                                            >
                                                ← Previous
                                            </button>

                                            <div className="page-numbers">
                                                {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                                                    let pageNum;
                                                    if (pagination.last_page <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (pagination.current_page <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (pagination.current_page >= pagination.last_page - 2) {
                                                        pageNum = pagination.last_page - 4 + i;
                                                    } else {
                                                        pageNum = pagination.current_page - 2 + i;
                                                    }

                                                    return (
                                                        <button
                                                            key={pageNum}
                                                            className={`page-number ${pagination.current_page === pageNum ? 'active' : ''}`}
                                                            onClick={() => handlePageChange(pageNum)}
                                                        >
                                                            {pageNum}
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            <button
                                                className="page-btn"
                                                disabled={pagination.current_page === pagination.last_page}
                                                onClick={() => handlePageChange(pagination.current_page + 1)}
                                            >
                                                Next →
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Offcanvas 
                show={showOffcanvas} 
                onHide={closeOffcanvas}
                placement="end"
                className="activity-log-offcanvas"
                style={{ width: '700px' }}
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>
                        <h4 className="mb-0">
                            Activity Log Details
                            {selectedLog && (
                                <Badge bg={getActionBadgeColor(selectedLog.action)} className="ms-2">
                                    {selectedLog.action}
                                </Badge>
                            )}
                        </h4>
                    </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    {selectedLog && (
                        <>
                            <div className="mb-3">
                                <nav>
                                    <div className="nav nav-tabs" id="nav-tab" role="tablist">
                                        <button
                                            className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('overview')}
                                        >
                                            Overview
                                        </button>
                                        <button
                                            className={`nav-link ${activeTab === 'changes' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('changes')}
                                        >
                                            Data Changes
                                        </button>
                                        <button
                                            className={`nav-link ${activeTab === 'raw' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('raw')}
                                        >
                                            Raw Data
                                        </button>
                                    </div>
                                </nav>
                            </div>

                            <div className="tab-content">
                                {activeTab === 'overview' && (
                                    <div className="tab-pane fade show active">
                                        <Row>
                                            <Col md={6}>
                                                <Card className="mb-3">
                                                    <Card.Header className="bg-light">
                                                        <h6 className="mb-0">Basic Information</h6>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <div className="info-item mb-2">
                                                            <strong className="d-block text-muted small">Model:</strong>
                                                            <span>{selectedLog.model_type.split('\\').pop()}</span>
                                                        </div>
                                                        <div className="info-item mb-2">
                                                            <strong className="d-block text-muted small">Model ID:</strong>
                                                            <span>{selectedLog.model_id}</span>
                                                        </div>
                                                        <div className="info-item mb-2">
                                                            <strong className="d-block text-muted small">Date:</strong>
                                                            <span>{formatDate(selectedLog.created_at)}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <strong className="d-block text-muted small">IP Address:</strong>
                                                            <code>{selectedLog.ip_address}</code>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                            <Col md={6}>
                                                <Card className="mb-3">
                                                    <Card.Header className="bg-light">
                                                        <h6 className="mb-0">User Information</h6>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <div className="user-info mb-3">
                                                            <div className="d-flex align-items-center">
                                                                <div className="avatar-circle me-3">
                                                                    {selectedLog.user?.name?.charAt(0) || 'U'}
                                                                </div>
                                                                <div>
                                                                    <div className="fw-bold">{selectedLog.user?.name || 'Unknown'}</div>
                                                                    <div className="text-muted small">{selectedLog.user?.email}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="info-item">
                                                            <strong className="d-block text-muted small">User Agent:</strong>
                                                            <small className="text-truncate d-block">
                                                                {selectedLog.user_agent}
                                                            </small>
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        </Row>
                                    </div>
                                )}

                                {activeTab === 'changes' && (
                                    <div className="tab-pane fade show active">
                                        {selectedLog.action === 'created' && (
                                            <Alert variant="success" className="mb-3">
                                                <Alert.Heading>
                                                    {selectedLog.model_type.includes('Transaction') ? 'Transaction Created' : 'Record Created'}
                                                </Alert.Heading>
                                                <p>
                                                    New {selectedLog.model_type.split('\\').pop()} record was created with ID: <strong>{selectedLog.model_id}</strong>
                                                </p>
                                                {selectedLog.new_data && (
                                                    <>
                                                        <hr />
                                                        <h6 className="mb-2">Important Fields:</h6>
                                                        <div className="initial-data">
                                                            {(() => {
                                                                const importantData = getImportantCreatedFields(selectedLog.new_data);
                                                                
                                                                if (Object.keys(importantData).length === 0) {
                                                                    return (
                                                                        <div className="text-muted text-center py-3">
                                                                            No important fields to display
                                                                        </div>
                                                                    );
                                                                }
                                                                
                                                                return Object.entries(importantData).map(([key, value]) => (
                                                                    <div key={key} className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                                                                        <span className="text-muted">{formatFieldName(key)}:</span>
                                                                        <span className="fw-medium text-end" style={{ maxWidth: '60%' }}>
                                                                            {formatFieldValue(key, value)}
                                                                        </span>
                                                                    </div>
                                                                ));
                                                            })()}
                                                        </div>
                                                    </>
                                                )}
                                            </Alert>
                                        )}

                                        {selectedLog.action === 'updated' && (
                                            <>
                                                {selectedLog.model_type.includes('Vendor') && (
                                                    <Alert variant="info" className="mb-3">
                                                        Vendor information was updated
                                                    </Alert>
                                                )}

                                                <Card className="border-0 shadow-sm">
                                                    <Card.Header className="bg-white border-bottom">
                                                        <h6 className="mb-0">Changed Fields Only</h6>
                                                    </Card.Header>
                                                    <Card.Body className="p-0">
                                                        <div className="table-responsive">
                                                            <Table hover className="mb-0">
                                                                <thead className="bg-light">
                                                                    <tr>
                                                                        <th width="35%">Field</th>
                                                                        <th width="32.5%" className="text-danger">Old Value</th>
                                                                        <th width="32.5%" className="text-success">New Value</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {(() => {
                                                                        const changedFields = getChangedFields(
                                                                            selectedLog.old_data, 
                                                                            selectedLog.new_data
                                                                        );
                                                                        
                                                                        if (Object.keys(changedFields).length === 0) {
                                                                            return (
                                                                                <tr>
                                                                                    <td colSpan="3" className="text-center text-muted py-4">
                                                                                        No fields were changed
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        }

                                                                        return Object.entries(changedFields).map(([field, values]) => {
                                                                            const { old, new: newVal } = values;
                                                                            const isFieldRemoved = old !== undefined && newVal === undefined;
                                                                            const isFieldAdded = old === undefined && newVal !== undefined;
                                                                            const isValueChanged = old !== undefined && newVal !== undefined && old !== newVal;

                                                                            return (
                                                                                <tr key={field} className="border-bottom">
                                                                                    <td>
                                                                                        <div className="d-flex align-items-center">
                                                                                            <span className="field-name fw-medium">
                                                                                                {formatFieldName(field)}
                                                                                            </span>
                                                                                            {isFieldAdded && (
                                                                                                <Badge bg="success" className="ms-2" size="sm">
                                                                                                    Added
                                                                                                </Badge>
                                                                                            )}
                                                                                            {isFieldRemoved && (
                                                                                                <Badge bg="danger" className="ms-2" size="sm">
                                                                                                    Removed
                                                                                                </Badge>
                                                                                            )}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td>
                                                                                        <div className={`old-value p-2 rounded ${isFieldRemoved || isValueChanged ? 'bg-light-danger' : ''}`}>
                                                                                            <span className={`${isFieldRemoved || isValueChanged ? 'text-danger fw-bold' : 'text-muted'}`}>
                                                                                                {formatFieldValue(field, old)}
                                                                                            </span>
                                                                                        </div>
                                                                                    </td>
                                                                                    <td>
                                                                                        <div className={`new-value p-2 rounded ${isFieldAdded || isValueChanged ? 'bg-light-success' : ''}`}>
                                                                                            <span className={`${isFieldAdded || isValueChanged ? 'text-success fw-bold' : 'text-muted'}`}>
                                                                                                {formatFieldValue(field, newVal)}
                                                                                            </span>
                                                                                        </div>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        });
                                                                    })()}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </Card.Body>
                                                    <Card.Footer className="bg-white border-top">
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <small className="text-muted">
                                                                <strong>Fields changed:</strong> {(() => {
                                                                    const changedFields = getChangedFields(
                                                                        selectedLog.old_data, 
                                                                        selectedLog.new_data
                                                                    );
                                                                    return Object.keys(changedFields).length;
                                                                })()}
                                                            </small>
                                                            <Button 
                                                                variant="outline-primary" 
                                                                size="sm"
                                                                onClick={() => {
                                                                    const modelName = selectedLog.model_type.split('\\').pop().toLowerCase();
                                                                    window.open(`/dashboard/${modelName}s/${selectedLog.model_id}`, '_blank');
                                                                }}
                                                            >
                                                                View Record
                                                            </Button>
                                                        </div>
                                                    </Card.Footer>
                                                </Card>
                                            </>
                                        )}

                                        {selectedLog.action === 'deleted' && (
                                            <Alert variant="danger" className="mb-3">
                                                <Alert.Heading>
                                                    Record Deleted
                                                </Alert.Heading>
                                                <p>
                                                    This record was permanently deleted
                                                </p>
                                                {selectedLog.old_data && (
                                                    <>
                                                        <hr />
                                                        <h6 className="mb-2">Deleted Data (Important Fields):</h6>
                                                        <div className="deleted-data">
                                                            {(() => {
                                                                const importantData = getImportantCreatedFields(selectedLog.old_data);
                                                                
                                                                if (Object.keys(importantData).length === 0) {
                                                                    return (
                                                                        <div className="text-muted text-center py-3">
                                                                            No important fields to display
                                                                        </div>
                                                                    );
                                                                }
                                                                
                                                                return Object.entries(importantData).map(([key, value]) => (
                                                                    <div key={key} className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                                                                        <span className="text-muted">{formatFieldName(key)}:</span>
                                                                        <span className="fw-medium text-end" style={{ maxWidth: '60%' }}>
                                                                            {formatFieldValue(key, value)}
                                                                        </span>
                                                                    </div>
                                                                ));
                                                            })()}
                                                        </div>
                                                    </>
                                                )}
                                            </Alert>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'raw' && (
                                    <div className="tab-pane fade show active">
                                        <Card>
                                            <Card.Body className="p-0">
                                                <pre className="m-0 p-3 bg-light" style={{ maxHeight: '500px', overflow: 'auto' }}>
                                                    {JSON.stringify(selectedLog, null, 2)}
                                                </pre>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </Offcanvas.Body>
            </Offcanvas>
        </div>
    );
};

export default ActivityLogs;